/** @jsx jsx */
import React, { useEffect, useMemo, useCallback, type PropsWithChildren } from 'react';
import { jsx } from '@compiled/react';
import { graphql, useFragment, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { AsyncGPAwarenessPill } from '@jira/servicedesk__jsm-grace-period-banner/src/AsyncGPAwarenessPill.tsx';
import { useTheme } from '@jira/navigation-apps__atlassian-navigation-theme/src/main.tsx';
import { isSupportedUserAgent as isMobileUserAgent } from '@jira/platform__is-mobile-user-agent/src/index.tsx';
import { useSetCustomThemeData } from '@jira/navigation-apps__common/src/controllers/custom-theme-context/index.tsx';
import { CustomThemeAnalytics } from '@jira/navigation-apps__common/src/controllers/custom-theme-analytics/index.tsx';
import { EditionAwareness } from '@jira/navigation-apps__sidebar-edition-awareness/src/ui/EditionAwareness.tsx';
import Nav4AppSwitcher from '@jira/navigation-apps__topnav-app-switcher/src/ui/index.tsx';
import type { UnitProfile } from '@atlassian/switcher/unit-tile-app-switcher-icon';
import AsyncCustomThemeAdminFlag from '@jira/theme__custom-theme-admin-flag/src/AsyncCustomThemeAdminFlag.tsx';
import {
	PACKAGE_NAME,
	TEAM_NAME,
} from '@jira/navigation-apps__atlassian-navigation-nav4-common/src/common/constants.tsx';
import { useRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { V4 } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/nav-state.tsx';
import { TOP } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/navigation-container.tsx';
import { GLOBAL } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/navigation-layer.tsx';
import { getSideNavToggleButtonAnalyticEventPayload } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import { IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/constants/index.tsx';
import { useLeftSidebarState } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/left-sidebar-controller/index.tsx';
import { topNav4UIStateResource } from '@jira/navigation-apps__resources/src/services/top-nav/index.tsx';
import { SoftphoneCallBarHost } from '@jira/customer-service__live-conversations-activity-status/src/SoftphoneCallBarHost.tsx';
import { getTokenValue } from '@atlaskit/tokens/get-token-value';
import { CustomTitle } from '@atlaskit/navigation-system/top-nav-items/custom-title';
import type { SideNavVisibilityChangeAnalyticsAttributes } from '@atlaskit/navigation-system';
import { SideNavToggleButton } from '@atlaskit/navigation-system/layout/side-nav';
import { TopNavStart, TopNavMiddle, TopNavEnd } from '@atlaskit/navigation-system/layout/top-nav';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import {
	ContextualAnalyticsData,
	fireUIAnalytics,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { JiraHoldoutCustomData } from '@atlassian/holdout-utils/jira-holdout';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useSiteTitle } from './site-title/useSiteTitle.tsx';
import type { TopNavigationQuery } from './__generated__/TopNavigationQuery.graphql';
import type {
	TopNavigation_slotsQuery$data,
	TopNavigation_slotsQuery$key,
} from './__generated__/TopNavigation_slotsQuery.graphql';
import { Nav4NotificationsData } from './notifications/Nav4NotificationsData.tsx';
import { Settings } from './settings/SettingsAction.tsx';
import { Search } from './search/index.tsx';
import { NavigationRouterListener } from './router-listener/index.tsx';
import { HelpButton } from './help/HelpButton.tsx';
import type { HelpButton_query$key } from './help/__generated__/HelpButton_query.graphql.ts';
import { ConversationAssistant } from './conversation-assistant/index.tsx';
import { ProfileNew } from './profile-new/ProfileNew.tsx';
import { ProductHomeWithQuery } from './product-home/ProductHomeWithQuery.tsx';
import { useReadyQueryReference } from './utils/useReadyQueryReference.tsx';

const sideNavToggleButtonCollapseLabelWithShortcut = defineMessage({
	id: 'atlassian-navigation.side-nav-toggle-button-collapse-label-with-shortcut',

	defaultMessage: 'Collapse sidebar [',
	description:
		'Label to use for side nav toggle button when it will collapse the side bar when clicked. It ends with a bracket character that reflects the keyboard shortcut that will toggle the side bar.',
});

const sideNavToggleButtonCollapseLabel = defineMessage({
	id: 'atlassian-navigation.side-nav-toggle-button-collapse-label',
	defaultMessage: 'Collapse sidebar',
	description: 'Label for the button that collapses the side navigation',
});

const sideNavToggleButtonExpandLabelWithShortcut = defineMessage({
	id: 'atlassian-navigation.side-nav-toggle-button-expand-label-with-shortcut',

	defaultMessage: 'Expand sidebar [',
	description:
		'Label to use for side nav toggle button when it will expand the side bar when clicked. It ends with a bracket character that reflects the keyboard shortcut that will toggle the side bar.',
});

const sideNavToggleButtonExpandLabel = defineMessage({
	id: 'atlassian-navigation.side-nav-toggle-button-expand-label',
	defaultMessage: 'Expand sidebar',
	description: 'Label for the button that expands the side navigation',
});

type HelpComponentProps = {
	QueryFragment: HelpButton_query$key | null;
};
function HelpComponent({ QueryFragment }: HelpComponentProps) {
	return <HelpButton QueryFragment={QueryFragment} />;
}

const Create = importCond<
	typeof import('@jira/navigation-apps__topnav-universal-create/src/UniversalCreate.tsx'),
	typeof import('./create/main.tsx')
>(
	'navx_onboarding_universal_create_in_jira',
	'@jira/navigation-apps__topnav-universal-create/src/UniversalCreate.tsx',
	'./create/main.tsx',
);

// ============================================================================
// EXPORTED COMPONENTS
// ============================================================================
export function TopNavigation(): JSX.Element | null {
	const { queryReference } = useRelayResource(topNav4UIStateResource);

	// Keep rendering the previous ref while a new one is in flight so route
	// changes (e.g. project navigation) don't flash the parent skeleton.
	const readyQueryReference = useReadyQueryReference(queryReference);

	if (!readyQueryReference) {
		return null;
	}

	return (
		<TopNavigationBoundary>
			<TopNavContent queryReference={readyQueryReference} />
		</TopNavigationBoundary>
	);
}

// ============================================================================
// INTERNAL COMPONENTS
// ============================================================================
export function TopNavContent({
	queryReference,
}: {
	queryReference: PreloadedQuery<TopNavigationQuery>;
}): JSX.Element {
	const data = usePreloadedQuery<TopNavigationQuery>(
		graphql`
			query TopNavigationQuery(
				$atlassianAccountId: ID!
				$cloudId: ID!
				$isAnonymous: Boolean!
				$isNavStateQuerySkipped: Boolean!
				$issueKey: String!
				$shouldCheckProjectTypeForIssue: Boolean!
				$projectKey: String!
				$shouldCheckProjectType: Boolean!
				$projectIdOrKey: String!
				$shouldFetchProjectTypeForSettingsAndEA: Boolean!
				$shouldFetchProjectType: Boolean!
				$shouldFetchCSMLiveChatAgent: Boolean!
				$skipHelpButtonRelayRead: Boolean!
				$collabContextRoutingAri: String
				$appSwitcherProductAri: ID!
				$skipAppSwitcherUnitProfile: Boolean!
			) {
				jira @skip(if: $isNavStateQuerySkipped) {
					navigationUIState(cloudId: $cloudId) {
						isLeftSidebarCollapsed
					}
				}
				...ProfileNew @alias(as: "profile") @skip(if: $isAnonymous)
				...Nav4NotificationsData
					@alias(as: "notifications")
					@arguments(collabContextRoutingAri: $collabContextRoutingAri)
					@skip(if: $isAnonymous)
				...ProductHomeWithQuery
				...EditionAwareness_query
				...SettingsAction_query
				...HelpButton_query @alias(as: "helpButton") @skip(if: $skipHelpButtonRelayRead)
				# NAVX-5411: spike measuring the cost of fetching + rendering a unit
				# tile as the app switcher trigger icon. Gated by
				# $skipAppSwitcherUnitProfile (computed from the experiment state in
				# the top-nav resource loader) so it adds no cost when disabled.
				appSwitcher_unitProfile(productAri: $appSwitcherProductAri)
					@skip(if: $skipAppSwitcherUnitProfile) {
					__typename
					... on AppSwitcherUnitProfile {
						unitName
						unitIconUrl
					}
					... on QueryError {
						message
						extensions {
							... on GenericQueryErrorExtension {
								statusCode
								errorType
							}
						}
					}
				}
			}
		`,
		queryReference,
	);

	const theme = useTheme();
	const cloudId = useCloudId();
	const isSiteAdmin = useIsSiteAdmin();
	const isMobileExperience = isMobileUserAgent();
	const setCustomThemeData = useSetCustomThemeData();

	// Set custom theme data for background and highlight colors
	const customThemeData = useMemo(
		() => ({
			hasCustomBGColor: !!theme?.mode.navigation.backgroundColor,
			hasCustomHighlightColor: getHasCustomHighlightColor(theme),
			cloudId,
		}),
		[cloudId, theme],
	);

	useEffect(() => {
		setCustomThemeData(customThemeData);
	}, [setCustomThemeData, customThemeData]);

	return (
		<>
			<CustomThemeAnalytics>
				{isMobileExperience && !fg('jira_use_full_navigation_on_mobile') ? (
					<TopNavMobile data={data} />
				) : (
					<TopNavDesktop data={data} />
				)}
				<NavigationRouterListener />
				{isSiteAdmin && <AsyncCustomThemeAdminFlag />}
			</CustomThemeAnalytics>
		</>
	);
}

function TopNavDesktop({ data }: { data: TopNavigationQuery['response'] }) {
	const isAnonymous = useIsAnonymous();
	const { formatMessage } = useIntl();

	const siteTitleData = useSiteTitle();
	const siteTitle = (siteTitleData.shouldShow && siteTitleData.value) || undefined;

	const setCustomThemeData = useSetCustomThemeData();

	// Set custom theme data for site title
	const desktopSiteTitleData = useMemo(
		() => ({
			hasCustomSiteTitle: !!siteTitle,
			customSiteTitleLength: siteTitle?.length || undefined,
		}),
		[siteTitle],
	);

	useEffect(() => {
		setCustomThemeData(desktopSiteTitleData);
	}, [setCustomThemeData, desktopSiteTitleData]);

	const attributes = useMemo(
		() => ({
			navigationLayer: GLOBAL,
			navigationContainer: TOP,
			navState: V4,
		}),
		[],
	);

	const onClickSideNavToggleButton = useCallback(
		(
			_: React.MouseEvent<HTMLElement>,
			analyticsEvent: UIAnalyticsEvent,
			_attributes: SideNavVisibilityChangeAnalyticsAttributes | undefined,
		) => {
			const payload = getSideNavToggleButtonAnalyticEventPayload(_attributes?.isSideNavVisible);
			fireUIAnalytics(analyticsEvent, payload);
		},
		[],
	);

	// NAVX-5411: spike measuring the cost of fetching + rendering a unit tile as
	// the app switcher trigger icon (see $skipAppSwitcherUnitProfile above).
	const unitProfile: UnitProfile | null = useMemo(() => {
		const profile = data.appSwitcher_unitProfile;
		if (profile?.__typename !== 'AppSwitcherUnitProfile') {
			return null;
		}
		return { unitName: profile.unitName, unitIconUrl: profile.unitIconUrl ?? null };
	}, [data.appSwitcher_unitProfile]);

	const { isCollapsed: isLeftSidebarCollapsed } = useLeftSidebarState();
	const defaultCollapsed = useMemo(() => {
		if (fg('navx-global-side-nav')) {
			return isLeftSidebarCollapsed ?? IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT;
		}
		return (
			data?.jira?.navigationUIState?.isLeftSidebarCollapsed ?? IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT
		);
	}, [data?.jira?.navigationUIState?.isLeftSidebarCollapsed, isLeftSidebarCollapsed]);

	const sideNavToggleButton = (
		<SideNavToggleButton
			defaultCollapsed={defaultCollapsed}
			onClick={onClickSideNavToggleButton}
			collapseLabel={formatMessage(
				fg('platform-dst-keep-desired-fhs-features')
					? sideNavToggleButtonCollapseLabel
					: sideNavToggleButtonCollapseLabelWithShortcut,
			)}
			expandLabel={formatMessage(
				fg('platform-dst-keep-desired-fhs-features')
					? sideNavToggleButtonExpandLabel
					: sideNavToggleButtonExpandLabelWithShortcut,
			)}
		/>
	);

	return (
		<ContextualAnalyticsData attributes={attributes}>
			<TopNavStart sideNavToggleButton={sideNavToggleButton}>
				<TopNavDesktopStartContent data={data} siteTitle={siteTitle} unitProfile={unitProfile} />
			</TopNavStart>
			<TopNavMiddle>
				<TopNavDesktopMiddleContent />
			</TopNavMiddle>
			<TopNavEnd>
				<TopNavDesktopEndContent data={data} isAnonymous={isAnonymous} />
			</TopNavEnd>
		</ContextualAnalyticsData>
	);
}

/**
 * The shared shape passed as `data` to every top-nav content component below.
 *
 * These components forward `data` to the six leaf fragment consumers
 * (`ProfileNew`, `EditionAwareness`, `HelpButton`, `Settings`,
 * `Nav4NotificationsData`, `ProductHomeWithQuery`), so they must accept EITHER
 * the full query response (`TopNavigationQuery['response']`, used by
 * `TopNavDesktop`/`TopNavMobile`) OR the `Query`-level aggregating fragment data
 * (`TopNavigation_slotsQuery$data`, used by `useTopNavigationSlots`).
 *
 * `ProfileNew`, `Nav4NotificationsData` and `HelpButton_query` are spread with
 * `@alias`, so both sources expose them as the named, `@skip`-nullable keys
 * `profile` / `notifications` / `helpButton` (each holding that consumer's
 * `$fragmentSpreads`). The remaining three consumers stay flattened into the
 * shared `$fragmentSpreads`. `Pick`ing this exact set off the fragment `$data`
 * captures what both sources share without hand-maintaining the leaf list, so it
 * stays in sync automatically when the fragment's spreads change.
 */
type TopNavContentData = Pick<
	TopNavigation_slotsQuery$data,
	'profile' | 'notifications' | 'helpButton' | ' $fragmentSpreads'
>;

/**
 * The children of the desktop `TopNavStart`, without the ADS `TopNavStart`
 * wrapper (and without the `sideNavToggleButton`, which `TopNavStart` takes as a
 * prop). Exposed so GlobalTopNavigation can render this content into its own
 * start slot via `UNSAFE__TopNavStartContent` without nesting a second
 * `TopNavStart`.
 */
export function TopNavDesktopStartContent({
	data,
	siteTitle,
	unitProfile,
}: {
	data: TopNavContentData;
	siteTitle: string | undefined;
	unitProfile: UnitProfile | null;
}): JSX.Element {
	return (
		<>
			<Nav4AppSwitcher unitProfile={unitProfile} />
			<TopNavAppHomeContent data={data} siteTitle={siteTitle} />
		</>
	);
}

/**
 * The Jira "app home" cluster (product home + optional custom site title).
 *
 * Rendered inside `TopNavStart` for the legacy standalone top nav, and provided
 * to `GlobalTopNavigation` via its `AppHomeSlot` (replacing GTN's default stub
 * App Home) when Jira renders into the platform top nav.
 */
export function TopNavAppHomeContent({
	data,
	siteTitle,
}: {
	data: TopNavContentData;
	siteTitle: string | undefined;
}): JSX.Element {
	return (
		<>
			<ProductHomeWithQuery queryReference={data} />
			{siteTitle && <CustomTitle>{siteTitle}</CustomTitle>}
		</>
	);
}

/**
 * The children of the desktop `TopNavMiddle`, without the ADS wrapper. See
 * {@link TopNavDesktopStartContent}.
 */
export function TopNavDesktopMiddleContent(): JSX.Element {
	return (
		<>
			<Search />
			<Create />
		</>
	);
}

/**
 * The children of the desktop `TopNavEnd`, without the ADS wrapper. See
 * {@link TopNavDesktopStartContent}.
 */
export function TopNavDesktopEndContent({
	data,
	isAnonymous,
}: {
	data: TopNavContentData;
	isAnonymous: boolean;
}): JSX.Element {
	return (
		<>
			{fg('jsm_gp_awareness_pill') && <AsyncGPAwarenessPill analyticsOrigin="navBar" />}
			<EditionAwareness editionAwarenessQueryFragment={data} />
			{!isAnonymous && <ConversationAssistant />}
			{!isAnonymous && data.notifications && (
				<Nav4NotificationsData queryReference={data.notifications} />
			)}
			<HelpComponent QueryFragment={isAnonymous ? null : (data.helpButton ?? null)} />
			<Settings settingsActionQueryFragment={data} />
			<ProfileNew queryReference={data.profile ?? null} />
		</>
	);
}

function TopNavMobile({ data }: { data: TopNavigationQuery['response'] }) {
	const siteTitleDataHook = useSiteTitle();
	const siteTitle = (siteTitleDataHook.shouldShow && siteTitleDataHook.value) || undefined;
	const setCustomThemeData = useSetCustomThemeData();

	// Set custom theme data for site title
	const mobileSiteTitleData = useMemo(
		() => ({
			hasCustomSiteTitle: !!siteTitle,
			customSiteTitleLength: siteTitle?.length || undefined,
		}),
		[siteTitle],
	);

	useEffect(() => {
		setCustomThemeData(mobileSiteTitleData);
	}, [setCustomThemeData, mobileSiteTitleData]);

	const attributes = useMemo(
		() => ({
			navigationLayer: GLOBAL,
			navigationContainer: TOP,
			navState: V4,
		}),
		[],
	);

	return (
		<ContextualAnalyticsData attributes={attributes}>
			<TopNavStart sideNavToggleButton={null}>
				<TopNavMobileStartContent data={data} siteTitle={siteTitle} />
			</TopNavStart>
			<TopNavMiddle>
				<TopNavMobileMiddleContent />
			</TopNavMiddle>
			<TopNavEnd>
				<TopNavMobileEndContent data={data} />
			</TopNavEnd>
		</ContextualAnalyticsData>
	);
}

/**
 * The children of the mobile `TopNavStart`, without the ADS wrapper. See
 * {@link TopNavDesktopStartContent}.
 */
export function TopNavMobileStartContent({
	data,
	siteTitle,
}: {
	data: TopNavContentData;
	siteTitle: string | undefined;
}): JSX.Element {
	return <TopNavAppHomeContent data={data} siteTitle={siteTitle} />;
}

/**
 * The children of the mobile `TopNavMiddle`, without the ADS wrapper. See
 * {@link TopNavDesktopStartContent}.
 */
export function TopNavMobileMiddleContent(): JSX.Element {
	return (
		<>
			<Search />
			<Create />
		</>
	);
}

/**
 * The children of the mobile `TopNavEnd`, without the ADS wrapper. See
 * {@link TopNavDesktopStartContent}.
 */
export function TopNavMobileEndContent({ data }: { data: TopNavContentData }): JSX.Element {
	const isAnonymous = useIsAnonymous();
	return (
		<>
			{fg('jsm_gp_awareness_pill') && <AsyncGPAwarenessPill analyticsOrigin="navBar" />}
			{!isAnonymous && data.notifications && (
				<Nav4NotificationsData queryReference={data.notifications} />
			)}
			<ProfileNew queryReference={data.profile ?? null} />
		</>
	);
}

/**
 * The Jira top-navigation content grouped into the three slot positions that
 * `GlobalTopNavigation` exposes (start / middle / end),
 * so Jira can migrate onto the platform top nav without nesting a second `TopNav`
 * and without needing to migrate the contents to specific slots up-front.
 *
 * The data is read via `useFragment` from a `Query`-level fragment
 * (`TopNavigation_slotsQuery`) that the consumer spreads into its own GlobalRoot
 * query (see `useJiraAppChromeQuery`). All variables the child fragments
 * need are provided as root operation variables by that query's resource.
 */
export type TopNavigationSlotsContent = {
	/**
	 * The Jira "app home" cluster (product home + custom title). Supplied to
	 * `GlobalTopNavigation` via `AppHomeSlot` so it replaces GTN's default stub
	 * App Home rather than duplicating it inside the start slot.
	 */
	appHome: React.JSX.Element;
	start: React.JSX.Element;
	middle: React.JSX.Element;
	end: React.JSX.Element;
};

/**
 * Reads Jira's top-nav data and returns the content grouped into the
 * `GlobalTopNavigation` slot positions (app home / start / middle / end).
 *
 * Exposed as a hook (rather than a render-prop component) so consumers can call
 * it at the top level of their own component and compose the results normally —
 * e.g. memoising `appHome` into a stable `AppHomeSlot`. Wrap the rendered
 * output in {@link TopNavigationBoundary} to retain the UFO segment and error
 * boundary.
 *
 * This is temporary until we start to move each component out of the start/middle/end
 * slots and put them directly into the GlobalTopNav, which will allow them to attach
 * their own fragment to the root query. Epic: https://hello.jira.atlassian.cloud/browse/NAVX-5408
 */
export function useTopNavigationSlots(
	queryRefFragment: TopNavigation_slotsQuery$key,
): TopNavigationSlotsContent {
	/* eslint-disable @atlassian/relay/no-wrapping-hooks, @atlassian/relay/graphql-naming */
	const queryData = useFragment<TopNavigation_slotsQuery$key>(
		graphql`
			fragment TopNavigation_slotsQuery on Query {
				...ProfileNew @alias(as: "profile") @skip(if: $isAnonymous)
				...Nav4NotificationsData
					@alias(as: "notifications")
					@arguments(collabContextRoutingAri: $collabContextRoutingAri)
					@skip(if: $isAnonymous)
				...ProductHomeWithQuery
				...EditionAwareness_query
				...SettingsAction_query
				...HelpButton_query @alias(as: "helpButton") @skip(if: $skipHelpButtonRelayRead)
			}
		`,
		queryRefFragment,
	);
	/* eslint-enable @atlassian/relay/no-wrapping-hooks, @atlassian/relay/graphql-naming */

	const isAnonymous = useIsAnonymous();
	const siteTitleData = useSiteTitle();
	const siteTitle = (siteTitleData.shouldShow && siteTitleData.value) || undefined;
	const isMobileExperience = isMobileUserAgent();
	const isMobile = isMobileExperience && !fg('jira_use_full_navigation_on_mobile');

	return isMobile
		? {
				appHome: <TopNavAppHomeContent data={queryData} siteTitle={siteTitle} />,
				// App home moves into GTN's `AppHomeSlot`; mobile has no other
				// start content.
				start: (
					<>
						{/* Mounted in the always-visible start slot so the fixed call bar survives narrow viewports (BEARS-2608). */}
						{!isAnonymous && <SoftphoneCallBarHost />}
					</>
				),
				middle: <TopNavMobileMiddleContent />,
				end: <TopNavMobileEndContent data={queryData} />,
			}
		: {
				appHome: <TopNavAppHomeContent data={queryData} siteTitle={siteTitle} />,
				// App home moves into GTN's `AppHomeSlot`; only the app switcher
				// remains as desktop start content.
				start: (
					<>
						{/* NAVX-5411: this slots path doesn't query `appSwitcher_unitProfile`
						(see the `TopNavigation_slotsQuery` fragment above), so there's no
						fetched unit profile to thread through here. */}
						<Nav4AppSwitcher unitProfile={null} />
						{/* Mounted in the always-visible start slot so the fixed call bar survives narrow viewports (BEARS-2608). */}
						{!isAnonymous && <SoftphoneCallBarHost />}
					</>
				),
				middle: <TopNavDesktopMiddleContent />,
				end: <TopNavDesktopEndContent data={queryData} isAnonymous={isAnonymous} />,
			};
}

/**
 * Wraps Jira's top-nav content in the UFO segment, holdout custom data and
 * `JSErrorBoundary` that the top nav requires. Pair with
 * {@link useTopNavigationSlots}.
 */
export function TopNavigationBoundary({ children }: PropsWithChildren): React.JSX.Element {
	return (
		<UFOSegment name="atlaskit-atlassian-navigation-nav4">
			<JiraHoldoutCustomData />
			<JSErrorBoundary
				id="AtlaskitAtlassianNavigationNav4"
				packageName={PACKAGE_NAME}
				teamName={TEAM_NAME}
				fallback="flag"
			>
				{children}
			</JSErrorBoundary>
		</UFOSegment>
	);
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Check if the theme has a custom highlight color
 */
function getHasCustomHighlightColor(theme: ReturnType<typeof useTheme>) {
	if (!theme) return false;

	// Default create button background color in Nav4 is #0052CC
	// #0052CC is a legacy brand blue. In the non-legacy tokens, the general replacement is:
	//   • Token name: color.link
	//   • CSS variable: var(--ds-link)
	//   • Current hex in atlassian-light: #0C66E4
	// If the create button background color is different from this value, we consider it as a custom highlight color
	const tokenHighlightColor = getTokenValue('color.link');

	return (
		theme.mode.create.default.backgroundColor !== tokenHighlightColor &&
		// Failsafe check in case the token value is not what we expect
		theme.mode.create.default.backgroundColor !== '#0052CC'
	);
}

export default TopNavigation;
